<template>
  <div class="pagination-wrapper">
    <div class="pagination-container">
      <!-- Left Side - Showing records info -->
      <div class="records-info" v-if="pagination.total_rows > 0">
        Showing 
        <span class="">{{ startRecord }}</span> 
        to 
        <span class="">{{ endRecord }}</span> 
        of 
        <span class="fw-semibold text-primary">{{ pagination.total_rows }}</span> 
        entries
      </div>

      <!-- Right Side - Pagination -->
      <nav v-if="pagination.total_pages > 1" class="pagination-nav">
        <ul class="pagination">
          <!-- Previous -->
          <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
            <button class="page-link" @click="$emit('page-change', pagination.current_page - 1)" :disabled="pagination.current_page === 1">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
          </li>
          
          <!-- First Page -->
          <li v-if="displayedPages[0] > 1" class="page-item">
            <button class="page-link" @click="$emit('page-change', 1)">1</button>
          </li>
          
          <!-- Ellipsis -->
          <li v-if="displayedPages[0] > 2" class="page-item disabled">
            <span class="page-link">...</span>
          </li>
          
          <!-- Page Numbers -->
          <li 
            v-for="page in displayedPages" 
            :key="page" 
            class="page-item"
            :class="{ active: page === pagination.current_page }"
          >
            <button class="page-link" @click="$emit('page-change', page)">
              {{ page }}
            </button>
          </li>
          
          <!-- Ellipsis -->
          <li v-if="displayedPages[displayedPages.length - 1] < pagination.total_pages - 1" class="page-item disabled">
            <span class="page-link">...</span>
          </li>
          
          <!-- Last Page -->
          <li v-if="displayedPages[displayedPages.length - 1] < pagination.total_pages" class="page-item">
            <button class="page-link" @click="$emit('page-change', pagination.total_pages)">
              {{ pagination.total_pages }}
            </button>
          </li>
          
          <!-- Next -->
          <li class="page-item" :class="{ disabled: pagination.current_page === pagination.total_pages }">
            <button class="page-link" @click="$emit('page-change', pagination.current_page + 1)" :disabled="pagination.current_page === pagination.total_pages">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Pagination',
  props: {
    pagination: {
      type: Object,
      required: true,
      default: () => ({
        current_page: 1,
        total_pages: 1,
        total_rows: 0,
        per_page: 10
      })
    }
  },
  computed: {
    startRecord() {
      return ((this.pagination.current_page - 1) * this.pagination.per_page) + 1
    },
    endRecord() {
      const end = this.pagination.current_page * this.pagination.per_page
      return end > this.pagination.total_rows ? this.pagination.total_rows : end
    },
    displayedPages() {
      const pages = []
      const totalPages = this.pagination.total_pages
      const currentPage = this.pagination.current_page
      const maxVisible = 5
      
      let start = Math.max(1, currentPage - Math.floor(maxVisible / 2))
      let end = Math.min(totalPages, start + maxVisible - 1)
      
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1)
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      
      return pages
    }
  }
}
</script>

<style scoped>
.pagination-wrapper {
  background: white;
  padding: 13px 8px;
}

.pagination-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  width: 100%; /* Add this */
}

/* Records Info - Left Side */
.records-info {
  font-size: 11px;
  color: #6b7280;
  letter-spacing: -0.2px;
  line-height: 1.4;
  flex-shrink: 0; /* Add this */
}

.fw-semibold {
  font-weight: 500;
}

.text-primary {
  color: #4f46e5;
  font-weight: 600;
}

/* Pagination - Right Side */
.pagination-nav {
  margin: 0;
  margin-left: auto; /* Keep this */
  flex-shrink: 0; /* Add this */
}

.pagination {
  display: flex;
  gap: 4px;
  margin: 0;
  padding: 0;
  list-style: none;
  justify-content: flex-end;
}

/* Rest of your CSS remains same */
.page-item {
  display: inline-block;
}

.page-link {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  padding: 0 6px;
  font-size: 11px;
  font-weight: 500;
  color: #4b5563;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
}

.page-link svg {
  stroke-width: 2.5;
}

.page-link:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: #d1d5db;
  transform: translateY(-1px);
}

.page-item.active .page-link {
  background: #4f46e5;
  border-color: #4f46e5;
  color: white;
  cursor: default;
  box-shadow: 0 1px 3px rgba(79, 70, 229, 0.2);
}

.page-item.active .page-link:hover {
  transform: none;
}

.page-item.disabled .page-link,
.page-link:disabled {
  color: #cbd5e1;
  cursor: not-allowed;
  background: #f9fafb;
  border-color: #e5e7eb;
}

/* Responsive */
@media (max-width: 640px) {
  .pagination-wrapper {
    padding: 12px 16px;
  }
  
  .pagination-container {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
  
  .records-info {
    font-size: 10px;
    text-align: left;
  }
  
  .pagination-nav {
    margin-left: 0;
    display: flex;
    justify-content: flex-end;
  }
  
  .pagination {
    justify-content: flex-end;
  }
  
  .page-link {
    min-width: 26px;
    height: 26px;
    padding: 0 5px;
    font-size: 10px;
  }
}
</style>